import { createElement } from 'react';
import PropTypes from 'prop-types';
import { BLOCKS, INLINES } from '@contentful/rich-text-types';

import Container from '../../Core/Container';
import RichText, { fragment } from '../../Core/RichText';
import LongFeatureCards from '../LongFeatureCards';
import Link from '../../Core/Link';
import IndividualBlade from '../IndividualBlades';
import MissionStatement from '../MissionStatement';
import ImageTitle from '../Cards/ImageTitle';

import {
  Heading,
  HeadingContainer,
  Subheading,
  ContentContainer,
  CTAWrapper,
  StyledImage,
  Disclaimer,
} from './styles';
import PCMAList from './PCMAList';

const heading = {
  renderNode: {
    [BLOCKS.PARAGRAPH]: (_, children) => <Heading>{children}</Heading>,
    [BLOCKS.HEADING_1]: (_, children) => <Heading>{children}</Heading>,
    [BLOCKS.HEADING_2]: (_, children) => <Heading>{children}</Heading>,
    [BLOCKS.HEADING_3]: (_, children) => <Heading>{children}</Heading>,
  },
};

const subheading = {
  renderNode: {
    [BLOCKS.PARAGRAPH]: (_, children) => <Subheading>{children}</Subheading>,
  },
};

const disclaimer = {
  renderNode: {
    [BLOCKS.PARAGRAPH]: (_, children) => <Disclaimer>{children}</Disclaimer>,
    [INLINES.HYPERLINK]: (node, children) => {
      const { data: linkCta } = node;
      return (
        <Link className="rich-text-link" to={linkCta.uri} fontSize="inherit">
          {children}
        </Link>
      );
    },
  },
};

const componentResolver = (data, className, theme) => {
  switch (data.component) {
    case 'pcma-feature-blocks':
      return createElement(LongFeatureCards, {
        data: { ...data, column: true, className },
        key: data.id,
      });
    case 'image-block':
      return <StyledImage key={data.id} src={data.imageDam} />;
    case 'pcma-list-section':
      return <PCMAList key={data.id} data={data} theme={theme} />;
    case 'title-with-statement':
      return (
        <MissionStatement key={data.id} data={data} className={className} />
      );
    case 'title-card':
      return <ImageTitle key={data.id} data={data} theme={theme} />;
    default:
      return null;
  }
};

const PCMASplitWidthContainer = ({ data }) => {
  if (!data) return null;

  const {
    anchorName,
    sectionContainerHeading,
    sectionContainerHeadingColor,
    sectionSubheading,
    columnCount,
    ctas,
    elements,
    contentAlignment,
    component,
    backgroundColor,
    legalCopy,
    theme,
  } = data;

  let flexDirectionColumn = false;

  if (elements && elements[0].component === 'title-card') {
    flexDirectionColumn = true;
  }

  return (
    <Container
      id={anchorName}
      as="section"
      backgroundColor={backgroundColor}
      className="pcma-split-width"
    >
      {(sectionContainerHeading || sectionSubheading) && (
        <HeadingContainer headingColor={[sectionContainerHeadingColor]}>
          <RichText document={sectionContainerHeading} options={heading} />
          <RichText document={sectionSubheading} options={subheading} />
        </HeadingContainer>
      )}
      {elements && (
        <ContentContainer
          className={component}
          flexDirectionColumn={flexDirectionColumn}
        >
          {elements.map((element, index) => {
            if (index >= columnCount) return null;

            return componentResolver(element, component, theme);
          })}
        </ContentContainer>
      )}
      {legalCopy && <RichText document={legalCopy} options={disclaimer} />}
      {ctas && (
        <CTAWrapper className={`${contentAlignment}`}>
          {ctas.map(cta =>
            cta.internal.type === 'ContentfulBlade' ? (
              <IndividualBlade key={cta.id} data={cta} />
            ) : (
              <Link key={cta.id} data={cta}>
                <RichText document={cta.label} options={fragment} />
              </Link>
            ),
          )}
        </CTAWrapper>
      )}
    </Container>
  );
};

PCMASplitWidthContainer.propTypes = {
  data: PropTypes.shape().isRequired,
};

export default PCMASplitWidthContainer;
